import { IpaType } from "../../phonemes/ipa";
import { dictionaryUS, englishAccentRules, rhoticAccent, EnglishAccentType, nonRhoticRules, commonFinalRules } from "../../ruleSets/accentRules";
import { englishToIpa } from "../../ruleSets/englishToIpa";
import { compileRuleSet } from "../../rules/compileRuleSet";
import { lexicalSets, lexicalSetsWikiOrdered, SetName } from "../../rules/findLexicalSets";
import { PhoneLengths } from "../../rules/lengthRules";
import { loadRule } from "../../rules/persistRules";
import { SetRules } from "../../rules/replaceLexicalSets";
import { Rule } from "../../rules/rule";
import { RuleSet, CompiledRuleSet } from "../../rules/ruleSet";
import { RulesRunner } from "../../rules/rulesRunner";
import { runRulesOnRaw } from "../../rules/runRules";
import { LexicalUnit } from "./LexicalUnits";
import { breakRhotic, nonRhoticUnit, phonemeForLength } from "./lsUnit";

const setOrder = lexicalSetsWikiOrdered;

/** track & handle settings for all lexical sets & rules */
export class ApplySets {
  private readonly setRules: SetRules;
  private units: LexicalUnit[] = [];
  private isNonRhotic: boolean;
  private ruleSet: RuleSet;
  private compiled: CompiledRuleSet;
  private pronounceSpelling = new RulesRunner(englishToIpa);

  constructor() {
    const all: Record<string, string> = {};
    lexicalSets.forEach(ls => all[ls.name] = ls.us);
    this.setRules = all as SetRules;

    // default to dictionary us
    const lengths = dictionaryUS.phonemeLengths?.map as Record<IpaType, number>;
    setOrder.forEach(name => {
      const ls = lexicalSets.filter(set => set.name === name)[0];
      this.units.push({ name: ls.name, phoneme: ls.us, length: lengths[ls.us] });
    });
    this.isNonRhotic = false;

    this.ruleSet = { ...dictionaryUS, rules2: commonFinalRules };
    [this.compiled] = compileRuleSet(this.ruleSet);
  }

  getRules() { return this.ruleSet; }

  /** for the named lexical set, get the current phoneme, how it should be displayed, & the phoneme from the reference accent */
  getSetPhonemes(setName: SetName, accent: string): { toEdit: IpaType, toDisplay: IpaType, ref: IpaType } {
    const toEdit = this.setRules[setName];
    const toDisplay = nonRhoticUnit(toEdit, this.isNonRhotic);
    const refAccent = englishAccentRules[accent];
    const refSets = refAccent ? refAccent.lexicalSets : null;
    const ref = refSets ? nonRhoticUnit(refSets[setName], this.isNonRhotic) : '';
    return { toEdit, toDisplay, ref };
  }

  /** get the phonemes for all lexical sets, stripping off trailing r's for non-rhotic accents */
  getAllPhonemes(): IpaType[] {
    const list: IpaType[] = [];
    this.units.forEach(u => {
      const p = nonRhoticUnit(u.phoneme, this.isNonRhotic);
      if (!list.includes(p)) {
        list.push(p);
      }
    });
    return list;
  }

  getUnits(): LexicalUnit[] { return this.units; }

  getAddPhonemeLength(): PhoneLengths | undefined {
    return this.compiled.addPhonemeLength;
  }

  /** replace all phonemes with the ones from the given accent */
  useAccent(accent: string, isCustom: boolean) {
    const result = fetchRules(accent, isCustom);
    if (result === null) return;
    const { ruleSet, isNonRhotic } = result;
    this.isNonRhotic = isNonRhotic ?? false;
    const lex = ruleSet.lexicalSets;
    if (!lex) return;

    const len = ruleSet.phonemeLengths?.map;

    const units: LexicalUnit[] = [];
    setOrder.forEach(name => {
      const phoneme = lex[name as SetName];
      this.setRules[name as SetName] = phoneme;

      const forLen = phonemeForLength(phoneme, isNonRhotic);
      if (len && len[forLen]) {
        units.push({ name, phoneme, length: len[forLen] });
      } else {
        units.push({ name, phoneme: lex[name as SetName] });
      }
    });
    this.units = units;

    // update rules
    const rules: Rule[] = isNonRhotic ? nonRhoticRules : [];
    this.ruleSet = { preprocess: ruleSet.preprocess, lexicalSets: ruleSet.lexicalSets, phonemeLengths: ruleSet.phonemeLengths, rules, rules2: commonFinalRules };
    [this.compiled] = compileRuleSet(this.ruleSet);
  }

  /** make adjustments needed to change whether or not accent is rhotic */
  changeRhotic(rhotic: boolean) {
    this.isNonRhotic = !rhotic;
    const ruleSet = this.ruleSet;

    // separate lengths of the rhotic lexical sets (like START) from the others
    const oldSets = ruleSet.lexicalSets as SetRules;
    const oldLengths = ruleSet.phonemeLengths?.map ?? {};
    let withoutRhotics: Record<string, number> = {};
    let justRhotics: Record<string, number> = {};
    lexicalSets.forEach(ls => {
      const p = oldSets[ls.name];
      if (ls.rhotic) {
        // copy length to adjusted phoneme
        const oldP = rhotic ? nonRhoticUnit(p, true) : p;
        const newP = rhotic ? p : nonRhoticUnit(p, true);
        justRhotics[newP] = oldLengths[oldP];
      }
      else {
        withoutRhotics[p] = oldLengths[p];
      }
    });
    // this should remove stale phonemes & prefer existing lengths over adjusted lengths
    const phonemeLengths = { map: { ...justRhotics, ...withoutRhotics } };

    // copy lengths to units
    this.units = this.units.map(u => {
      const isSetRhotic = lexicalSets.filter(ls => u.name === ls.name)[0].rhotic;
      if (!isSetRhotic) {
        return u;
      }
      const newP = nonRhoticUnit(u.phoneme, !rhotic);
      return { ...u, length: phonemeLengths.map[newP] };
    });

    // break apart any single-symbol rhotics
    this.units = this.units.map(u => {
      const phoneme = breakRhotic(u.phoneme);
      return { ...u, phoneme };
    });
    let newSets = { ...oldSets };
    setOrder.forEach(name => {
      const phoneme = breakRhotic(newSets[name]);
      newSets[name] = phoneme;
    });

    // adjust (non)rhotic rules
    const rules: Rule[] = rhotic ? [] : nonRhoticRules;
    this.ruleSet = { preprocess: ruleSet.preprocess, lexicalSets: newSets, phonemeLengths, rules, rules2: commonFinalRules };
    [this.compiled] = compileRuleSet(this.ruleSet);
  }

  /** change the phoneme used for a set */
  changePhoneme(name: SetName, phoneme: string) {
    this.setRules[name] = phoneme;

    // if phoneme is already used, use its length
    const phonemeUnits = this.units.filter(u => u.phoneme === phoneme);
    const phonemeLen = phonemeUnits.length > 0 ? phonemeUnits[0].length : undefined;
    if (phonemeLen) {
      this.units = this.units.map(u => u.name !== name ? u : { ...u, phoneme, length: phonemeLen });
    } else {
      // phoneme not in use
      const setUnit = this.units.filter(u => u.name === name);
      if (setUnit && setUnit[0] && setUnit[0].length) {
        // copy unit's length to new phoneme
        const phonemeLengths = { map: { ...this.ruleSet.phonemeLengths?.map as Record<string, number> } };
        phonemeLengths.map[phoneme] = setUnit[0].length;
        this.ruleSet = { ...this.ruleSet, phonemeLengths };
        // should probably also get rid of the old phoneme's length if unused
      }

      this.units = this.units.map(u => u.name !== name ? u : { ...u, phoneme });
    }

    // update rules
    const lexicalSets = { ...this.ruleSet.lexicalSets as SetRules };
    lexicalSets[name] = phoneme;
    this.ruleSet = { ...this.ruleSet, lexicalSets };
    [this.compiled] = compileRuleSet(this.ruleSet);
  }

  /** change the length of the given phoneme */
  changeLength(phoneme: IpaType, length: number) {
    // update every set that uses the phoneme
    this.units = this.units.map(u => u.phoneme !== phoneme ? u : { ...u, length });

    // store length for non-rhotic part
    phoneme = (this.isNonRhotic ? nonRhoticUnit(phoneme, this.isNonRhotic) : phoneme);

    // update rules
    const phonemeLengths = { map: { ...this.ruleSet.phonemeLengths?.map as Record<string, number> } };
    phonemeLengths.map[phoneme] = length;
    this.ruleSet = { ...this.ruleSet, phonemeLengths };
    [this.compiled] = compileRuleSet(this.ruleSet);
  }

  runRules(input: string): string {
    const [output] = runRulesOnRaw(input, this.compiled, this.fallback);
    return output;
  }

  private fallback = (word: string): string => {
    const result = this.pronounceSpelling.runRulesOnIpa(word);
    return result.ipa;
  }
}

/** either load custom rule or use built-in rule */
function fetchRules(accent: string, isCustom: boolean): { ruleSet: RuleSet, isNonRhotic?: boolean } | null {
  if (isCustom) {
    return loadRule(accent);
  } else {
    const ruleSet = englishAccentRules[accent];
    return ruleSet === undefined ? null : { ruleSet, isNonRhotic: !rhoticAccent[accent as EnglishAccentType] };
  }
}

/** report on whether the accent is rhotic */
export function isAccentRhotic(accent: string, isCustom: boolean): boolean {
  if (isCustom) {
    const result = loadRule(accent);
    return result && result.isNonRhotic ? false : true;
  } else {
    return rhoticAccent[accent as EnglishAccentType];
  }
}
